import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminTopBar, ViewScroll, Panel, StatCard, SectionLabel, Sym } from '../../ui';
import { ContentToolbar } from '../../ui/ContentToolbar';
import type { DeviceModelOutput, CreateDeviceModelInput, UpdateDeviceModelInput } from '@grosync/common';
import { fetchDevices } from '../../api/devices';
import { ModelFormModal, type ModelDraft } from './components/ModelFormModal';
import { sensorMeta, growthFieldMeta, deviceTypeLabel, normalizeGrowthFields } from './catalog';
import { useDeviceModels } from './useDeviceModels';
import { useLocalDeviceModelMigration } from './migrateLocalModels';
import { LoadingSpinner } from '../../components/LoadingSpinner';

/* ============================================================================
 * 장비모델 — 모델 카탈로그 + 모델 추가/수정 (GroVision admin redesign)
 * ========================================================================= */

type CSS = React.CSSProperties;

type ModelTone = 'primary' | 'secondary' | 'tertiary';

interface SensorView {
  type: string;
  displayName: string;
  unit: string;
}
interface ViewModel {
  id: string;
  name: string;
  code: string;
  tagline: string;
  icon: string;
  tone: ModelTone;
  desc: string;
  deviceType: string;
  growth: boolean;
  growthFields: string[];
  sensors: SensorView[];
  comm: string;
  battery: string;
  interval: string;
  firmware: string;
  builtin: boolean;
  createdAt?: string;
}

/* ---- Server model → ViewModel mapping ---------------------------------- */
function serverToViewModel(m: DeviceModelOutput): ViewModel {
  const sensors: SensorView[] = m.sensors.map((s) => ({
    type: s.sensorType,
    displayName: s.displayName || sensorMeta(s.sensorType).label,
    unit: s.unit,
  }));
  const growthFields = normalizeGrowthFields(m.growthFields);
  return {
    id: m.id,
    name: m.name,
    code: m.code,
    tagline: m.tagline || (m.growthEnabled ? '센서 + 생육 분석형' : '환경 센서형'),
    icon: m.growthEnabled ? 'eco' : 'sensors',
    tone: m.growthEnabled ? 'primary' : m.isBuiltin ? 'secondary' : 'tertiary',
    desc: m.description || '',
    deviceType: m.deviceType,
    growth: m.growthEnabled,
    growthFields,
    sensors,
    comm: m.comm || 'LTE-M / Wi-Fi',
    battery: m.battery || '—',
    interval: m.interval || '1분',
    firmware: m.firmware || 'v1.0.0',
    builtin: m.isBuiltin,
    createdAt: m.createdAt,
  };
}

const modelSensorCount = (m: ViewModel): number => m.sensors.length;

/* ---- Small shared bits ------------------------------------------------ */
const SensorCapsule = ({ s }: { s: SensorView }): React.ReactElement => {
  const meta = sensorMeta(s.type);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 9999, background: 'var(--md-sys-color-surface-container-high)', font: '500 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
      <Sym name={meta.icon} size={16} fill={1} style={{ color: meta.tone }} />
      {s.displayName}
      {/* 영문 파라미터 (ESP32 sensorType) */}
      <span style={{ font: '500 10px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-primary)', opacity: 0.85 }}>{s.type}</span>
      {s.unit && (
        <span style={{ font: '400 11px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{s.unit}</span>
      )}
    </span>
  );
};

const GrowthCapsule = ({
  label,
  icon,
  active,
}: {
  label: string;
  icon: string;
  active: boolean;
}): React.ReactElement => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      height: 32,
      padding: '0 13px',
      borderRadius: 9999,
      background: active ? 'var(--md-sys-color-primary-container)' : 'transparent',
      border: active ? 0 : '1px dashed var(--md-sys-color-outline-variant)',
      opacity: active ? 1 : 0.45,
      font: '500 13px/1 var(--gs-font-kr)',
      color: active
        ? 'var(--md-sys-color-on-primary-container)'
        : 'var(--md-sys-color-on-surface-variant)',
    }}
  >
    <Sym name={icon} size={16} fill={active ? 1 : 0} />
    {label}
  </span>
);

type DeployTone = 'ok' | 'warn' | 'off' | 'default';
const DeployStat = ({
  label,
  value,
  unit,
  tone = 'default',
}: {
  label: string;
  value: React.ReactNode;
  unit?: string;
  tone?: DeployTone;
}): React.ReactElement => {
  const col =
    tone === 'warn'
      ? 'var(--gs-warning)'
      : tone === 'off'
        ? '#9AA095'
        : 'var(--md-sys-color-on-surface)';
  return (
    <div
      style={{
        padding: '12px 14px',
        borderRadius: 14,
        background: 'var(--md-sys-color-surface-container-low)',
        border: '1px solid var(--md-sys-color-outline-variant)',
      }}
    >
      <div style={{ font: '500 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
        {label}
      </div>
      <div
        className="gs-num"
        style={{ font: '700 22px/26px var(--gs-font-kr)', letterSpacing: '-0.5px', color: col, marginTop: 4 }}
      >
        {value}
        {unit && (
          <span
            style={{
              font: '500 12px/14px var(--gs-font-kr)',
              color: 'var(--md-sys-color-on-surface-variant)',
              marginLeft: 2,
            }}
          >
            {unit}
          </span>
        )}
      </div>
    </div>
  );
};

/* ---- ModelHero card ---------------------------------------------------- */
interface ModelStats {
  total: number;
  rate: number;
}
const ModelHero = ({
  m,
  stats,
  onRemove,
  onEdit,
}: {
  m: ViewModel;
  stats: ModelStats;
  onRemove?: (id: string) => void;
  onEdit?: (id: string) => void;
}): React.ReactElement => {
  const envUnits = m.sensors.length;
  const envTypes = m.sensors.map((s) => s.displayName).join(' · ');
  const grad =
    m.tone === 'primary'
      ? 'linear-gradient(135deg, var(--gs-primary-40), var(--gs-primary-30))'
      : m.tone === 'tertiary'
        ? 'linear-gradient(135deg, var(--gs-tertiary-40), var(--gs-tertiary-30))'
        : 'linear-gradient(135deg, var(--gs-secondary-40), var(--gs-secondary-30))';
  const specs: { k: string; v: string }[] = [
    { k: '장비유형', v: deviceTypeLabel(m.deviceType) },
    { k: '측정 항목', v: `환경 ${envUnits}개${m.growth ? ` + 생육 ${m.growthFields.length}종` : ''}` },
    { k: '센서 구성', v: envTypes || '—' },
    { k: '생육 촬영', v: m.growth ? '매일 12:00 자동' : '미지원' },
    { k: '전송 주기', v: m.interval || '1분' },
    { k: '통신', v: m.comm || 'LTE-M / Wi-Fi' },
    { k: '배터리', v: m.battery || '—' },
  ];
  const rateTone: DeployTone = !stats.total
    ? 'off'
    : stats.rate >= 90
      ? 'ok'
      : stats.rate >= 60
        ? 'warn'
        : 'off';
  const deleteDisabled = stats.total > 0 || m.builtin;
  return (
    <div
      style={{
        background: 'var(--md-sys-color-surface-container-lowest)',
        border: '1px solid var(--md-sys-color-outline-variant)',
        borderRadius: 20,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ padding: '22px 24px', background: grad, color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            right: -30,
            top: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.10)',
          }}
        />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sym name={m.icon} size={32} fill={1} style={{ color: '#fff' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ font: '700 22px/26px var(--gs-font-kr)', letterSpacing: '-0.3px' }}>{m.name}</span>
              {m.builtin && (
                <span
                  style={{
                    height: 20,
                    padding: '0 8px',
                    borderRadius: 9999,
                    background: 'rgba(255,255,255,0.22)',
                    font: '600 10px/20px var(--gs-font-kr)',
                  }}
                >
                  기본
                </span>
              )}
              {!m.builtin && m.createdAt && Date.now() - new Date(m.createdAt).getTime() < 7 * 86400000 && (
                <span
                  style={{
                    height: 20,
                    padding: '0 8px',
                    borderRadius: 9999,
                    background: 'rgba(255,255,255,0.22)',
                    font: '600 10px/20px var(--gs-font-kr)',
                  }}
                >
                  NEW
                </span>
              )}
            </div>
            <div style={{ font: '500 13px/18px var(--gs-font-kr)', opacity: 0.92, marginTop: 3 }}>{m.tagline}</div>
          </div>
          <span
            style={{
              height: 26,
              padding: '0 12px',
              borderRadius: 9999,
              background: 'rgba(255,255,255,0.20)',
              font: '600 12px/26px var(--gs-font-kr)',
            }}
          >
            FW {m.firmware || 'v2.4.1'}
          </span>
          {!m.builtin && onEdit && (
            <button type="button" onClick={() => onEdit(m.id)} title="모델 수정"
              style={{ width: 34, height: 34, borderRadius: 9999, border: 0, cursor: 'pointer', background: 'rgba(255,255,255,0.18)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sym name="edit" size={18} />
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(m.id)}
              disabled={deleteDisabled}
              title={
                stats.total > 0
                  ? `사용 중인 장비 ${stats.total}대가 있어 삭제할 수 없습니다`
                  : m.builtin
                    ? '기본 제공 모델은 삭제할 수 없습니다'
                    : '모델 삭제'
              }
              style={{
                width: 34,
                height: 34,
                borderRadius: 9999,
                border: 0,
                cursor: deleteDisabled ? 'not-allowed' : 'pointer',
                background: 'rgba(255,255,255,0.18)',
                color: '#fff',
                opacity: deleteDisabled ? 0.4 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Sym name="delete" size={18} />
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: '20px 24px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <p style={{ margin: 0, font: '400 14px/22px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
          {m.desc}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <DeployStat label="등록 대수" value={stats.total} unit="대" />
          <DeployStat label="센서 채널" value={envUnits} unit="개" />
          <DeployStat
            label="가동률"
            value={stats.total ? stats.rate : '–'}
            unit={stats.total ? '%' : ''}
            tone={rateTone}
          />
        </div>

        <div>
          <SectionLabel style={{ marginBottom: 10 }}>환경 센서 구성</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {m.sensors.map((s) => (<SensorCapsule key={s.type} s={s} />))}
          </div>
        </div>

        <div>
          <SectionLabel style={{ marginBottom: 10 }}>
            생육 분석 {m.growth ? `· ${m.growthFields.length}종` : ''}
          </SectionLabel>
          {m.growth && m.growthFields.length ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {m.growthFields.map((v) => {
                const g = growthFieldMeta(v);
                return <GrowthCapsule key={v} label={g.label} icon={g.icon} active />;
              })}
            </div>
          ) : (
            <div style={{ marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
              <Sym name="info" size={14} />생육 분석 미지원 모델
            </div>
          )}
        </div>

        <div>
          <SectionLabel style={{ marginBottom: 10 }}>제품 사양</SectionLabel>
          <div style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--md-sys-color-outline-variant)' }}>
            {specs.map((s, i) => (
              <div
                key={s.k}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 14px',
                  background:
                    i % 2
                      ? 'var(--md-sys-color-surface-container-low)'
                      : 'var(--md-sys-color-surface-container-lowest)',
                  borderBottom:
                    i === specs.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                }}
              >
                <span
                  style={{
                    font: '500 13px/18px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                    width: 84,
                    flexShrink: 0,
                  }}
                >
                  {s.k}
                </span>
                <span
                  style={{
                    flex: 1,
                    font: '500 13px/18px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface)',
                    textAlign: 'right',
                  }}
                >
                  {s.v}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---- Comparison table (dynamic over all models) ----------------------- */
const Comparison = ({ models }: { models: ViewModel[] }): React.ReactElement => {
  const allTypes: string[] = [];
  for (const m of models) for (const s of m.sensors) if (!allTypes.includes(s.type)) allTypes.push(s.type);
  const find = (m: ViewModel, type: string): SensorView | undefined => m.sensors.find((x) => x.type === type);
  const cols = `1.6fr repeat(${models.length}, 1fr)`;
  const cell = (center = true, head = false): CSS => ({
    padding: '12px 18px',
    borderBottom: '1px solid var(--md-sys-color-outline-variant)',
    borderLeft: center ? '1px solid var(--md-sys-color-outline-variant)' : 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: center ? 'center' : 'flex-start',
    font: `${head ? 700 : 500} 13px/18px var(--gs-font-kr)`,
    color: 'var(--md-sys-color-on-surface)',
  });
  const specRows: { label: string; key: 'battery' | 'interval' | 'firmware' }[] = [
    { label: '배터리', key: 'battery' },
    { label: '전송 주기', key: 'interval' },
    { label: '펌웨어', key: 'firmware' },
  ];
  return (
    <Panel title="모델 비교" subtitle="센서 구성 · 생육 · 사양" pad={false}>
      <div style={{ overflowX: 'auto' }} className="gs-scroll-hide">
        <div style={{ display: 'grid', gridTemplateColumns: cols, minWidth: 420 + models.length * 150 }}>
          <div style={{ ...cell(false, true), background: 'var(--md-sys-color-surface-container-low)' }}>항목</div>
          {models.map((m) => (
            <div key={m.id} style={{ ...cell(true, true), background: 'var(--md-sys-color-surface-container-low)' }}>{m.name}</div>
          ))}

          {allTypes.map((type) => {
            const meta = sensorMeta(type);
            return (
              <React.Fragment key={type}>
                <div style={cell(false)}>
                  <Sym name={meta.icon} size={16} fill={1} style={{ color: meta.tone, marginRight: 8 }} />
                  {meta.label}
                  <span style={{ font: '500 10px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-primary)', opacity: 0.8, marginLeft: 6 }}>{type}</span>
                </div>
                {models.map((m) => {
                  const s = find(m, type);
                  return (
                    <div key={m.id} style={cell(true)}>
                      {s ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <Sym name="check_circle" size={18} fill={1} style={{ color: 'var(--gs-primary-50)' }} />
                          {s.unit && <span style={{ font: '400 11px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{s.unit}</span>}
                        </span>
                      ) : (
                        <Sym name="remove" size={16} style={{ color: 'var(--md-sys-color-outline)' }} />
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}

          <div style={cell(false)}>생육 분석</div>
          {models.map((m) => (
            <div key={m.id} style={cell(true)}>
              {m.growth ? (
                <Sym name="check_circle" size={20} fill={1} style={{ color: 'var(--gs-primary-50)' }} />
              ) : (
                <Sym name="remove" size={16} style={{ color: 'var(--md-sys-color-outline)' }} />
              )}
            </div>
          ))}

          {specRows.map((row, ri, arr) => {
            const last = ri === arr.length - 1;
            return (
              <React.Fragment key={row.label}>
                <div style={{ ...cell(false), borderBottom: last ? 0 : '1px solid var(--md-sys-color-outline-variant)' }}>{row.label}</div>
                {models.map((m) => (
                  <div key={m.id} style={{ ...cell(true), borderBottom: last ? 0 : '1px solid var(--md-sys-color-outline-variant)', font: '600 13px/18px var(--gs-font-kr)' }}>
                    {m[row.key] || '—'}
                  </div>
                ))}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </Panel>
  );
};

/* ---- View -------------------------------------------------------------- */
export default function DeviceModelsView(): React.ReactElement {
  useLocalDeviceModelMigration();
  const { models: rawModels, isLoading, create, update, remove } = useDeviceModels();
  const { data: devices = [] } = useQuery({ queryKey: ['devices'], queryFn: fetchDevices });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<DeviceModelOutput | null>(null);

  const models: ViewModel[] = useMemo(
    () => rawModels.map(serverToViewModel),
    [rawModels],
  );

  const statsByModel = useMemo(() => {
    const toUiOk = (s: string): boolean => s !== 'OFFLINE';
    const map = new Map<string, ModelStats>();
    for (const m of models) {
      const matched = devices.filter((d) => d.deviceModelId === m.code);
      const total = matched.length;
      const online = matched.filter((d) => toUiOk(d.status)).length;
      map.set(m.id, { total, rate: total ? Math.round((online / total) * 100) : 0 });
    }
    return map;
  }, [models, devices]);

  const totalChannels = models.reduce((n, m) => n + modelSensorCount(m), 0);
  const distinctFw = [...new Set(models.map((m) => m.firmware || 'v1.0.0'))];
  const totalDevices = devices.length;
  const nextDefaultName = `모델 ${models.length + 1}`;

  const handleSave = (draft: ModelDraft): void => {
    if (editing) void update(editing.id, draft as UpdateDeviceModelInput);
    else void create(draft as CreateDeviceModelInput);
  };
  const handleEdit = (id: string): void => {
    const found = rawModels.find((m) => m.id === id);
    if (found) setEditing(found);
  };
  const handleRemove = (id: string): void => {
    const m = models.find((x) => x.id === id);
    if (!m) return;
    // 빌트인 모델은 서버에서 삭제 불가
    if (m.builtin) return;
    // 해당 모델로 만들어진 장비가 하나라도 있으면 삭제 불가
    const inUse = statsByModel.get(m.id)?.total ?? 0;
    if (inUse > 0) {
      window.alert(
        `'${m.name}' 모델은 현재 ${inUse}대의 장비에서 사용 중이라 삭제할 수 없습니다.\n` +
          `해당 장비를 다른 모델로 변경하거나 삭제한 뒤 다시 시도하세요.`,
      );
      return;
    }
    if (!window.confirm(`'${m.name}' 모델을 삭제하시겠습니까?`)) return;
    void remove(m.id);
  };

  const addTile = (
    <button
      type="button"
      onClick={() => setAdding(true)}
      style={{
        minHeight: 280,
        borderRadius: 20,
        cursor: 'pointer',
        border: '2px dashed var(--md-sys-color-outline-variant)',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        color: 'var(--md-sys-color-on-surface-variant)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = 'var(--md-sys-color-primary)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--md-sys-color-outline-variant)';
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          background: 'var(--md-sys-color-surface-container-high)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Sym name="add" size={28} style={{ color: 'var(--md-sys-color-primary)' }} />
      </div>
      <span style={{ font: '600 15px/20px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>
        새 장비모델 추가
      </span>
      <span style={{ font: '400 12px/16px var(--gs-font-kr)' }}>센서 타입·수량을 직접 구성</span>
    </button>
  );

  return (
    <>
      <AdminTopBar
        title="장비모델"
        subtitle={`${models.length}개 모델 운영 · 전체 ${totalDevices}대 배포`}
      />

      <ViewScroll gap={18}>
        <ContentToolbar
          actions={
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => setAdding(true)}
                style={{
                  height: 42,
                  padding: '0 18px',
                  borderRadius: 9999,
                  border: 0,
                  cursor: 'pointer',
                  background: 'var(--md-sys-color-primary)',
                  color: 'var(--md-sys-color-on-primary)',
                  font: '600 14px/1 var(--gs-font-kr)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Sym name="add" size={18} />
                모델 추가
              </button>
            </div>
          }
        />

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16 }}>
              <StatCard
                icon="memory"
                label="운영 모델"
                value={models.length}
                unit="종"
                tone="secondary"
                sub={models.map((m) => m.name).slice(0, 3).join(' · ')}
              />
              <StatCard icon="sensors" label="총 센서 채널" value={totalChannels} unit="개" tone="primary" sub="전 모델 합산" />
              <StatCard
                icon="eco"
                label="생육 분석 모델"
                value={models.filter((m) => m.growth).length}
                unit="종"
                tone="primary"
                sub="영상 분석 지원"
              />
              <StatCard
                icon="memory"
                label="펌웨어 버전"
                value={`${distinctFw.length}종`}
                tone="muted"
                sub={`모델별 관리 · ${distinctFw.join(' · ')}`}
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
                gap: 18,
                alignItems: 'start',
              }}
            >
              {models.map((m) => (
                <ModelHero
                  key={m.id}
                  m={m}
                  stats={statsByModel.get(m.id) ?? { total: 0, rate: 0 }}
                  onEdit={m.builtin ? undefined : handleEdit}
                  onRemove={handleRemove}
                />
              ))}
              {addTile}
            </div>

            <Comparison models={models} />
          </>
        )}
      </ViewScroll>

      {(adding || editing) && (
        <ModelFormModal
          initial={editing}
          defaultName={nextDefaultName}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSave={handleSave}
        />
      )}
    </>
  );
}
